import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Banner } from '@els/els-react--banner';
import { Modal } from '@els/els-react--modal';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { Dropdown } from '@els/els-react--dropdown';
import { Loader } from '@els/els-react--loader';
import GSFNSubstances from '../GSFNSubstances';

import './GSFNDialog.scss';

function GSFNDialog({ errorInfoMessage, errorText, options, onInputEnter, isLoading, substances, onSubstanceSelect, selfClose }) {
  const [inputValue, setInputValue] = useState('');
  const [option, setOption] = useState(options[0]);
  const queryValidatorRef = useRef(null);
  const hadErrorRef = useRef(false);
  const MIN_QUERY_LENGTH = 3;

  const inputEnterHandler = (value) => {
    const searchValue = value ?? inputValue;
    setInputValue(searchValue);
    if (searchValue && searchValue.length >= MIN_QUERY_LENGTH && option?.value) {
      onInputEnter(searchValue, option.value);
    }
  };

  const validateInput = useCallback(() => (errorText ? [{ isValid: false, message: errorText }] : null), [errorText]);

  useEffect(() => {
    if (errorText || hadErrorRef.current) {
      queryValidatorRef.current?.validate(inputValue, true);
      hadErrorRef.current = !!errorText;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorText]);

  return (
    <Modal
      id="gsfn-dialog"
      className="react-dialogs-scrollable insert-structure-from-name-dialog"
      label="Create structure template from name"
      onClose={selfClose}
      width="50rem">
      <div className="insert-structure-from-name-dialog__body">
        <h3 id="dialog-title" className="insert-structure-from-name-dialog__title">
          Create structure template from name
        </h3>
        <div className="insert-structure-from-name-dialog__content">
          <div className="insert-structure-from-name-dialog__content__options">
            <Dropdown
              id="structureFromName-options"
              className="insert-structure-from-name-dialog__content__options__dropdown"
              label="Select option"
              name="structureFromNameOption"
              options={options}
              selected={option}
              onChange={setOption}
              showSelectedAsLabel
              buttonProps={{
                type: 'link',
                'aria-label': `Select structure from name option, current ${option?.label}`,
                'data-e2e': 'insert-structure-from-name-dialog-option-dropdown',
              }}
            />
          </div>
          <div className="insert-structure-from-name-dialog__content__search">
            <ValidatedTextInput
              data-e2e="search-structure"
              id="structureFromName-query"
              className="o-els-flex-layout__item o-els-flex-layout__item--grow"
              ref={queryValidatorRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target?.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  inputEnterHandler(e.target?.value);
                }
              }}
              shouldHideLabel
              aria-describedby={errorText ? 'structureFromName-query-message-inner structureFromName-banner' : undefined}
              label="Enter a chemical name, CAS-RN, InChiKey or SMILES"
              validator={validateInput}
              onlyValidateWhenTouched={false}
              iconRight={{
                sprite: 'Search',
                id: 'search-icon',
                children: 'Search for structure',
                a11y: {
                  name: 'Search',
                },
              }}
              iconRightCallback={() => inputEnterHandler()}
            />
            {errorText && (
              <span className="u-els-hide-visually" role="alert">
                {errorText}
              </span>
            )}
          </div>
        </div>
        {errorText && (
          <Banner
            id="structureFromName-banner"
            className="insert-structure-from-name-dialog__banner"
            type="info"
            size="small"
            isVisible
            isIconVisible
            hideCloseLabel>
            {errorInfoMessage}
          </Banner>
        )}
        {isLoading && (
          <Loader id="gsfn-dialog-loader" isVisible isBlocking={false} isSmall messages={{ LOADING_MESSAGE: 'Loading structure' }} />
        )}
        {substances && <GSFNSubstances substances={substances} onSubstanceSelect={onSubstanceSelect} />}
      </div>
    </Modal>
  );
}

GSFNDialog.propTypes = {
  errorInfoMessage: PropTypes.string,
  errorText: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      isDisabled: PropTypes.bool,
    })
  ).isRequired,
  onInputEnter: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  substances: PropTypes.array,
  onSubstanceSelect: PropTypes.func.isRequired,
  selfClose: PropTypes.func.isRequired,
};

export const typeOfGSFNDialog = 'typeOfGSFNDialog';
export default GSFNDialog;
